/**
 * The wire shapes of the account second factor (#282), mirroring
 * `API.Core/Models/Authentication/SecondFactorModels.cs`. JSON is camelCase; **request bodies are PascalCase**,
 * matching every other body this SPA sends (`{Enabled: …}`, `{IdentityId: …}`) — ASP.NET binds either, and one
 * convention beats two.
 *
 * Kept as interfaces rather than classes: nothing here has behaviour, and a class would invite a constructor
 * that silently invents defaults for fields the server is the only authority on.
 */

/**
 * What the profile screen may know about an account's second factor.
 *
 * Note what is deliberately absent, and stays absent: the TOTP secret and the recovery-code values. The server
 * hands out counts and timestamps only — see the DTO's own remarks. Do not "enrich" this model with anything
 * derived from a secret.
 */
export interface SecondFactorStatus {
  /** A **confirmed** factor exists. An abandoned enrolment reads as `false`. */
  enabled: boolean;
  /** When enrolment was confirmed (ISO), so the holder can recognise their own act. Null when never enrolled. */
  confirmedUtc: string | null;
  /** When the factor last verified anything (ISO). Null when it never has. */
  lastUsedUtc: string | null;
  /** How many recovery codes are unused. */
  remainingRecoveryCodes: number;
  /** How many the current set was issued with — the denominator of "7 von 10". */
  recoveryCodesIssued: number;
  /**
   * When a recovery code was last spent (ISO), or null.
   *
   * Shown because it is a security signal only the account holder can interpret: either they lost their phone,
   * or somebody else has their printout. A remaining count alone never asks that question.
   */
  lastRecoveryCodeUsedUtc: string | null;
  /** What the company insists on — the `AccountSecurityLevel` **name**, never its ordinal. */
  requiredLevel: AccountSecurityLevelName;
  /** The company demands more than a password, i.e. the factor is not optional here. */
  required: boolean;
  /**
   * The holder may remove the factor. False while the company requires it — removing would strand the account
   * in the restricted session at the next sign-in. The server refuses it either way; this only decides whether
   * a button that could not work is shown at all.
   */
  canDisable: boolean;
}

/**
 * The `AccountSecurityLevel` names as the server spells them
 * (`CheqMe.Server.Contracts/Authorization/AccountSecurityLevel.cs`).
 *
 * A string union rather than a numeric enum mirror, because the server deliberately sends the **name**: a
 * numeric mirror here would follow any renumbering silently, which is the failure the server-side comment on
 * `RequiredLevel` exists to prevent.
 */
export type AccountSecurityLevelName = 'Password' | 'TwoFactor' | 'Federated';

/**
 * A started enrolment — everything needed to add the account to an authenticator app, returned **once**.
 *
 * There is no way to fetch it again by design: re-displaying a secret would make it a password, i.e. something
 * readable back out of our store, which is the one property the whole factor rests on not having. A closed tab
 * means starting a new enrolment, which replaces the pending secret.
 */
export interface SecondFactorEnrolment {
  /** The shared secret, base32 — for typing in by hand when a camera is not an option. */
  secret: string;
  /** The `otpauth://` URI, for a device that can follow a link. */
  uri: string;
  /** A `data:image/png;base64,…` QR code of {@link uri}. Bound straight into `<img [src]>`. */
  qrCode: string;
}

/**
 * A freshly issued set of recovery codes, returned **once** — at confirmation, or on a regeneration.
 *
 * Regenerating replaces the whole set rather than topping it up, so "the codes you have are the codes that
 * work" is true at every instant. The UI must therefore never merge two sets, and must never store one.
 */
export interface RecoveryCodeSet {
  /** The codes in display form. Never persisted anywhere by the client. */
  codes: string[];
}

/**
 * What a sign-in is told when the password was right and a second factor is still owed. Rides the **error
 * details** of the refused login (403 `E_AUTH_TWO_FACTOR_REQUIRED`), not a success body — nothing is granted yet.
 */
export interface SecondFactorChallenge {
  /** The opaque challenge to hand back with the code. Not a credential on its own. */
  challenge: string;
  /** When it stops being accepted (ISO). */
  expiresUtc: string;
  /**
   * Whether the account still has unused recovery codes, so the screen knows whether to offer that route.
   *
   * A *count* is deliberately not sent: this response is reachable by anybody holding the password, and
   * "two codes left" is a useful thing for an attacker to learn about a target they are about to phish.
   */
  recoveryCodesAvailable: boolean;
}

/**
 * The session's account-security verdict, mirroring the server's `account_security` claim — exactly the shape
 * and the two-value vocabulary of the existing `formsGate` claim, on purpose: the gate, the guard branch, the
 * interceptor branch and the remediation screen are the same machine with a different subject.
 */
export const AccountSecurity = {
  /** Nothing owed — an ordinary session. Also the reading of an ABSENT claim. */
  Ok: 'ok',
  /** A second factor is owed: the session may reach the remediation screen and nothing else. */
  Restricted: 'restricted'
} as const;

export type AccountSecurityValue = typeof AccountSecurity[keyof typeof AccountSecurity];
